import { Input } from "@/app/global-components/input";
import { validateFileExtensions } from "@/lib/utils";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import {
  AppBar,
  Avatar,
  Tab,
  Tabs,
  Typography,
  Box,
  Button,
  Chip,
} from "@mui/material";
import { Edit2Icon, Trash2Icon, Camera, Save, X, Trash } from "lucide-react";
import PropTypes from "prop-types";
import {
  ChangeEvent,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import PersonelInfo from "./components/personel_info";
import useStore from "@/hooks/use-store";
import { useNavigate } from "react-router-dom";
import { routes } from "@/consts";
import { LANG } from "@/types";

const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "svg"];

type StoreType = {
  name: string;
  email: string;
  website: string;
  commercialRegister: string;
  bioEn: string;
  bioAr: string;
};

interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  value: number | string;
  index: number | string;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wrapped-tabpanel-${index}`}
      aria-labelledby={`wrapped-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index: string) {
  return {
    id: `wrapped-tab-${index}`,
    "aria-controls": `wrapped-tabpanel-${index}`,
  };
}

function StorePage() {
  const [store, setStore] = useState<any>(null);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<StoreType>({
    name: "",
    email: "",
    website: "",
    commercialRegister: "",
    bioEn: "",
    bioAr: "",
  });
  const { getMyStore, createStore, updateStore, deleteStore, isLoading } =
    useStore();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const isRTL = locale === LANG.AR;

  const handleRemoveImage = (type: "pp" | "cover") => {
    if (!isEditMode) return;
    if (type === "pp") {
      setProfileImage(null);
      setProfilePreview(null);
    } else {
      setCoverImage(null);
      setCoverPreview(null);
    }
  };

  const handleFileChange = async (
    e: ChangeEvent<HTMLInputElement>,
    type: "pp" | "cover"
  ) => {
    if (!isEditMode) return;

    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error(t("buyer_pages.profile.file_type_err"));
      return;
    }
    const selectedFile = selectedFiles[0];
    const { isValid } = validateFileExtensions(
      [selectedFile],
      ALLOWED_EXTENSIONS
    );
    if (!isValid) {
      toast.error(t("buyer_pages.profile.file_type_err"));
    } else {
      if (type === "pp") {
        setProfileImage(selectedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfilePreview(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setCoverImage(selectedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          setCoverPreview(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      }
    }
  };
  const [value, setValue] = useState("one");
  const handleChange = (_: any, newValue: string) => {
    setValue(newValue);
  };

  const handleDelete = async () => {
    if (store?._id) {
      await deleteStore(store._id);
      navigate(routes.store);
    }
  };

  const handleSave = async () => {
    if (!isEditMode) return;

    try {
      const storeData = {
        ...formData,
        imgUrl: profileImage,
        coverUrl: coverImage,
        companyId: store?.companyId,
        userId: store?.userId,
      };

      if (store?._id) {
        await updateStore(storeData, store._id);
        setIsEditMode(false);
      } else {
        const res = await createStore(storeData);
        if (res.isSuccess) {
          setStore(res.data);
        }
        setIsEditMode(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const res = async () => {
      const res = await getMyStore();
      if (!res.isSuccess) {
        setIsEditMode(true);
        return;
      }
      setStore(res.data);
      if (res.data) {
        setFormData({
          name: res.data.name || "",
          email: res.data.email || "",
          website: res.data.website || "",
          commercialRegister: res.data.commercialRegister || "",
          bioEn: res.data.bioEn || "",
          bioAr: res.data.bioAr || "",
        });
      }
    };
    res();
  }, []);
  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? "rtl" : "ltr"}>
      {/* Hero Section */}
      <div className="relative h-[400px] w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 z-10"></div>
        {(coverPreview || store?.coverUrl) && (
          <img
            src={coverPreview || store?.coverUrl}
            alt="cover"
            className="w-full h-full object-cover"
          />
        )}
        {isEditMode && (
          <div className="absolute inset-0 group z-20">
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
              <div className="flex gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <label htmlFor="cc" className="cursor-pointer">
                  <Input
                    type={"file"}
                    id="cc"
                    accept={"image/*"}
                    onChange={(e) => handleFileChange(e, "cover")}
                    className={"hidden"}
                  />
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 hover:bg-white/30 transition-colors">
                    <Camera className="text-2xl text-white" />
                  </div>
                </label>
                {(coverPreview || store?.coverUrl) && (
                  <button
                    className="bg-white/20 backdrop-blur-sm rounded-full p-3 hover:bg-white/30 transition-colors"
                    onClick={() => handleRemoveImage("cover")}
                  >
                    <Trash2Icon className="text-2xl text-white" />
                  </button>
                )}
              </div>
            </div>
            {/* Edit indicator */}
            <div
              className={`absolute bg-[#18181b] text-white px-3 py-1 rounded-full text-sm font-medium z-30 ${
                isRTL ? "top-4 left-4" : "top-4 right-4"
              }`}
            >
              {t("store.edit.editCover")}
            </div>
          </div>
        )}
        <div
          className={`absolute bottom-8 z-20 ${isRTL ? "right-8" : "left-8"}`}
        >
          <div
            className={`flex items-end space-x-4 ${
              isRTL ? "space-x-reverse" : ""
            }`}
          >
            <div className="relative group">
              <div className="w-32 h-32 border-4 border-white rounded-full overflow-hidden shadow-lg">
                {profilePreview || store?.imgUrl ? (
                  <img
                    src={profilePreview || store?.imgUrl}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Avatar className="!w-full !h-full bg-gray-200" />
                )}
              </div>
              {isEditMode && (
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 rounded-full flex items-center justify-center z-10">
                  <div
                    className={`opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-2 ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <label htmlFor="pp" className="cursor-pointer">
                      <Input
                        type={"file"}
                        accept={"image/*"}
                        id="pp"
                        onChange={(e) => handleFileChange(e, "pp")}
                        className="hidden"
                      />
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors">
                        <Camera className="text-lg text-white" />
                      </div>
                    </label>
                    {(profilePreview || store?.imgUrl) && (
                      <button
                        className="bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors"
                        onClick={() => handleRemoveImage("pp")}
                      >
                        <Trash2Icon className="text-lg text-white" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {/* Edit indicator for profile */}
              {isEditMode && (
                <div
                  className={`absolute bg-[#18181b] text-white px-2 py-1 rounded-full text-xs font-medium z-20 ${
                    isRTL ? "-top-2 -left-2" : "-top-2 -right-2"
                  }`}
                >
                  {t("store.edit.edit")}
                </div>
              )}
            </div>
            <div className="mb-4">
              <h1 className="text-4xl font-bold text-white mb-2">
                {store?.name || "My Store"}
              </h1>
              <div className="flex items-center space-x-2">
                <Chip
                  label={
                    isEditMode
                      ? t("store.edit.editMode")
                      : t("store.edit.viewMode")
                  }
                  color={isEditMode ? "primary" : "default"}
                  sx={{
                    backgroundColor: isEditMode ? "#18181b" : "#6b7280",
                    color: "white",
                    fontWeight: "medium",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Edit Mode Toggle */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div
              className={`flex items-center justify-between ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t("store.edit.storeManagement")}
                </h2>
                <p className="text-gray-600">
                  {isEditMode
                    ? t("store.edit.editModeDescription")
                    : t("store.edit.viewModeDescription")}
                </p>
              </div>
              <Button
                variant={isEditMode ? "outlined" : "contained"}
                onClick={() => setIsEditMode(!isEditMode)}
                sx={{
                  backgroundColor: isEditMode ? "transparent" : "#18181b",
                  color: isEditMode ? "#18181b" : "white",
                  borderColor: "#18181b",
                  borderRadius: "12px",
                  padding: "12px 24px",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: isEditMode ? "#18181b" : "#18181b",
                    color: isEditMode ? "white" : "white",
                  },
                }}
                startIcon={
                  isEditMode ? <X size={20} /> : <Edit2Icon size={20} />
                }
              >
                {isEditMode
                  ? t("store.edit.cancelEdit")
                  : t("store.edit.editStore")}
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <AppBar
            position="static"
            sx={{
              backgroundColor: "white",
              boxShadow: "none",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="store management tabs"
              sx={{
                "& .MuiTab-root": {
                  color: "#6b7280",
                  fontWeight: "500",
                  textTransform: "none",
                  fontSize: "16px",
                  "&.Mui-selected": {
                    color: "#18181b",
                    fontWeight: "600",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#18181b",
                  height: "3px",
                },
              }}
            >
              <Tab
                value="one"
                label={t("store.edit.personalInformation")}
                wrapped
                {...a11yProps("one")}
              />
            </Tabs>
          </AppBar>

          <TabPanel value={value} index={"one"}>
            <div className="p-6">
              <PersonelInfo
                data={store}
                setStore={setStore}
                profileImage={profileImage}
                coverImage={coverImage}
                isEditMode={isEditMode}
                onEditModeChange={setIsEditMode}
                formData={formData}
                setFormData={setFormData}
              />
            </div>
          </TabPanel>
        </div>

        {/* Action Buttons */}
        {isEditMode && (
          <div
            className={`mt-8 flex justify-end gap-4 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <Button
              variant="outlined"
              onClick={handleDelete}
              disabled={isLoading}
              sx={{
                borderColor: "#ef4444",
                color: "#ef4444",
                borderRadius: "12px",
                padding: "12px 24px",
                fontWeight: "600",
                "&:hover": {
                  borderColor: "#dc2626",
                  backgroundColor: "#fef2f2",
                },
                "&:disabled": {
                  borderColor: "#d1d5db",
                  color: "#9ca3af",
                },
              }}
              startIcon={<Trash size={20} />}
            >
              {isLoading ? t("actions.deleting") : t("actions.delete")}{" "}
              {t("general.store")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading}
              sx={{
                backgroundColor: "#18181b",
                color: "white",
                borderRadius: "12px",
                padding: "12px 32px",
                fontWeight: "600",
                fontSize: "16px",
                "&:hover": {
                  backgroundColor: "#18181b",
                  opacity: 0.9,
                },
                "&:disabled": {
                  backgroundColor: "#d1d5db",
                  color: "#9ca3af",
                },
              }}
              startIcon={<Save size={20} />}
            >
              {isLoading ? t("actions.saving") : t("store.edit.saveChanges")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StorePage;
